export type Timeframe =
  | 'Today'
  | 'Last 7 Days'
  | 'Last 30 Days'
  | 'Month to Date'
  | 'Custom Range'

export interface MetricRecord {
  id: string
  time: string
  name: string
  status: string
  duration?: string
  notes?: string
  amount?: string
  rate?: string
  score?: string
}

export interface PipelineRecord {
  id: string
  name: string
  status: string
  amount: number
  rate: string
  closeDate: string
  notes: string
}

export interface MetricDetailData {
  chartData: { label: string; value: number }[]
  records: MetricRecord[]
}

export interface MetricData {
  name: string
  value: number | string
  trend: 'up' | 'down' | 'neutral'
  trendValue: string
  sparklineData: { value: number }[]
  details: MetricDetailData
  benchmark: number
}

export interface FunnelStage {
  name: string
  percentage: number
  benchmark: number
  status: 'green' | 'yellow' | 'red'
}

export interface PipelineData {
  activePipeline: number
  fundingsMTD: number
  projectedCurrentMonth: number
  projectedNextMonth: number
  details?: {
    activeRecords: PipelineRecord[]
    fundingRecords: PipelineRecord[]
    projectedCurrentRecords: PipelineRecord[]
    projectedNextRecords: PipelineRecord[]
  }
}

export interface UrgentActionData {
  finalCDSigningTimes: number
  suspendedLoans: number
  initialCDsNotSigned: number
  initialDisclosuresNotSigned: number
}

export interface OpportunitiesData {
  retentionRecapture: {
    count: number
    volume: number
  }
  hotLeads: {
    count: number
    volume: number
  }
}

// Company conversion benchmarks (percentage targets)
export const FUNNEL_BENCHMARKS: Record<string, number> = {
  'Pull → App': 70,
  'App → Sub': 65,
  'Sub → Sign': 75,
  'Sign → Lock': 70,
}

export const getFunnelStatus = (
  percentage: number,
  benchmark: number,
): 'green' | 'yellow' | 'red' => {
  if (percentage >= benchmark) return 'green'
  if (percentage >= benchmark * 0.85) return 'yellow'
  return 'red'
}

const buildFunnelStage = (name: string, percentage: number): FunnelStage => {
  const benchmark = FUNNEL_BENCHMARKS[name] || 70
  return {
    name,
    percentage,
    benchmark,
    status: getFunnelStatus(percentage, benchmark),
  }
}

export interface ProjectionData {
  lockTarget: number
  lockActual: number
  commissionTarget: number
  commissionActual: number
  pipelineCoverage: number
}

export interface ScorecardMetric {
  name: string
  actual: number
  target: number
  status: 'above' | 'below' | 'meet'
}

export interface LeaderboardEntry {
  rank: number
  name: string
  dials: number
  conversations: number
  creditPulls: number
  applications: number
  locks: number
  submissions: number
  score: number
  isCurrentUser?: boolean
}

export interface GamificationData {
  streaks: {
    daily: number
    pull: number
    submission: number
    lock: number
  }
  badges: {
    name: string
    earned: boolean
    icon: string
  }[]
  closerOfWeek: {
    name: string
    locks: number
  }
}

export interface DashboardData {
  metrics: MetricData[]
  funnel: FunnelStage[]
  projections: ProjectionData
  scorecard: ScorecardMetric[]
  leaderboard: LeaderboardEntry[]
  gamification: GamificationData
  pipeline: PipelineData
  urgentActions: UrgentActionData
  opportunities: OpportunitiesData
}

const generateSparkline = (
  base: number,
  volatility: number,
  trend: 'up' | 'down',
) => {
  const data = []
  let current = base
  for (let i = 0; i < 10; i++) {
    const change = (Math.random() - (trend === 'up' ? 0.3 : 0.7)) * volatility
    current = Math.max(0, current + change)
    data.push({ value: current })
  }
  return data
}

// --- Detail Data Generators ---

const NAMES = [
  'John Doe',
  'Jane Smith',
  'Robert Johnson',
  'Emily Davis',
  'Michael Wilson',
  'Sarah Brown',
  'David Miller',
  'Jessica Taylor',
  'James Anderson',
  'Linda Thomas',
]
const getRandomName = () => NAMES[Math.floor(Math.random() * NAMES.length)]
const getRandomId = () => Math.random().toString(36).substring(2, 9)
const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(val)

const generateChartData = (timeframe: Timeframe, totalValue: number) => {
  const data = []
  let labels: string[] = []

  if (timeframe === 'Today') {
    labels = [
      '8AM',
      '9AM',
      '10AM',
      '11AM',
      '12PM',
      '1PM',
      '2PM',
      '3PM',
      '4PM',
      '5PM',
    ]
  } else if (timeframe === 'Last 7 Days') {
    labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  } else {
    labels = Array.from({ length: 10 }, (_, i) => `Day ${i * 3 + 1}`)
  }

  // Distribute totalValue somewhat randomly across labels
  let remaining = totalValue
  for (let i = 0; i < labels.length; i++) {
    if (i === labels.length - 1) {
      data.push({ label: labels[i], value: Math.max(0, Math.round(remaining)) })
    } else {
      const chunk = Math.random() * (remaining / (labels.length - i)) * 1.5
      const val = Math.max(0, Math.round(chunk))
      data.push({ label: labels[i], value: val })
      remaining -= val
    }
  }
  return data
}

const generateRecords = (
  metricName: string,
  timeframe: Timeframe,
  count: number,
): MetricRecord[] => {
  const records: MetricRecord[] = []
  const numRecords =
    timeframe === 'Today' ? Math.min(count, 8) : Math.min(count, 12)

  for (let i = 0; i < numRecords; i++) {
    const time =
      timeframe === 'Today'
        ? `${Math.floor(Math.random() * 8) + 8}:00 AM`
        : `Oct ${Math.floor(Math.random() * 30) + 1}`
    const name = getRandomName()

    if (metricName === 'Dials') {
      const statuses = ['Connected', 'Voicemail', 'No Answer', 'Busy']
      records.push({
        id: getRandomId(),
        time,
        name,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        duration: `${Math.floor(Math.random() * 5)}m ${Math.floor(Math.random() * 60)}s`,
      })
    } else if (metricName === 'Conversations') {
      const topics = [
        'Rate Inquiry',
        'Pre-Qual',
        'Follow-up',
        'Application Help',
      ]
      records.push({
        id: getRandomId(),
        time,
        name,
        status: 'Completed',
        notes: topics[Math.floor(Math.random() * topics.length)],
        duration: `${Math.floor(Math.random() * 20) + 5}m ${Math.floor(Math.random() * 60)}s`,
      })
    } else if (metricName === 'Credit Pulls') {
      const statuses = ['Completed', 'Pending', 'Failed']
      const scores = ['720-740', '680-700', '780-800', '620-640', '750-770']
      records.push({
        id: getRandomId(),
        time,
        name,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        score: scores[Math.floor(Math.random() * scores.length)],
      })
    } else if (metricName === 'Applications') {
      const statuses = ['In Progress', 'Submitted', 'Docs Needed', 'Withdrawn']
      records.push({
        id: getRandomId(),
        time,
        name,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        amount: formatCurrency(Math.floor(Math.random() * 500000) + 200000),
      })
    } else if (metricName === 'Submissions') {
      const statuses = ['Submitted', 'Under Review', 'Approved', 'Conditions']
      records.push({
        id: getRandomId(),
        time,
        name,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        amount: formatCurrency(Math.floor(Math.random() * 500000) + 200000),
      })
    } else if (metricName === 'Locks') {
      const periods = ['30-day', '45-day', '60-day']
      records.push({
        id: getRandomId(),
        time,
        name,
        status: 'Locked',
        rate: `${(Math.random() * 2 + 5).toFixed(3)}%`,
        amount: formatCurrency(Math.floor(Math.random() * 500000) + 200000),
        notes: periods[Math.floor(Math.random() * periods.length)],
      })
    }
  }
  return records
}

const generatePipelineRecords = (
  type: 'active' | 'funded' | 'projected',
  totalVolume: number,
): PipelineRecord[] => {
  const records: PipelineRecord[] = []
  const numRecords = Math.max(5, Math.floor(totalVolume / 450000)) // Average loan size ~450k

  let remainingVolume = totalVolume

  for (let i = 0; i < numRecords; i++) {
    const name = getRandomName()

    // Distribute volume
    let amount = 0
    if (i === numRecords - 1) {
      amount = remainingVolume
    } else {
      amount = Math.floor(
        (remainingVolume / (numRecords - i)) * (0.8 + Math.random() * 0.4),
      )
      remainingVolume -= amount
    }

    let status = ''
    let closeDate = ''
    let notes = ''

    const d = new Date()

    if (type === 'active') {
      const statuses = [
        'Processing',
        'Underwriting',
        'Cond. Approval',
        'Clear to Close',
      ]
      status = statuses[Math.floor(Math.random() * statuses.length)]
      d.setDate(d.getDate() + Math.floor(Math.random() * 45))
      closeDate = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
      notes = status === 'Cond. Approval' ? 'Waiting on appraisal' : 'On track'
    } else if (type === 'funded') {
      status = 'Funded'
      d.setDate(d.getDate() - Math.floor(Math.random() * 30))
      closeDate = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
      notes = 'Closed successfully'
    } else {
      status = 'Locked'
      d.setDate(d.getDate() + 15 + Math.floor(Math.random() * 45))
      closeDate = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
      notes = 'Rate locked, processing docs'
    }

    records.push({
      id: getRandomId(),
      name,
      status,
      amount,
      rate: `${(5.5 + Math.random() * 2).toFixed(3)}%`,
      closeDate,
      notes,
    })
  }

  // Sort by amount descending
  return records.sort((a, b) => b.amount - a.amount)
}

// Company benchmark baselines (daily targets)
const DAILY_BENCHMARKS: Record<string, number> = {
  Dials: 40,
  Conversations: 25,
  'Credit Pulls': 15,
  Applications: 10,
  Submissions: 6,
  Locks: 4,
}

const TIMEFRAME_MULTIPLIERS: Record<Timeframe, number> = {
  Today: 1,
  'Last 7 Days': 7,
  'Last 30 Days': 30,
  'Month to Date': 15,
  'Custom Range': 20,
}

export const getBenchmarkForMetric = (
  metricName: string,
  timeframe: Timeframe,
): number => {
  const daily = DAILY_BENCHMARKS[metricName] || 10
  return daily * TIMEFRAME_MULTIPLIERS[timeframe]
}

export type BenchmarkStatus = 'green' | 'yellow' | 'red'

export const getBenchmarkStatus = (
  value: number | string,
  benchmark: number,
): BenchmarkStatus => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  const ratio = numValue / benchmark
  if (ratio >= 1.0) return 'green'
  if (ratio >= 0.75) return 'yellow'
  return 'red'
}

const buildMetric = (
  name: string,
  value: number,
  trend: 'up' | 'down' | 'neutral',
  trendValue: string,
  sparkBase: number,
  sparkVol: number,
  timeframe: Timeframe,
): MetricData => {
  return {
    name,
    value,
    trend,
    trendValue,
    sparklineData: generateSparkline(sparkBase, sparkVol, trend),
    details: {
      chartData: generateChartData(timeframe, value),
      records: generateRecords(name, timeframe, value),
    },
    benchmark: getBenchmarkForMetric(name, timeframe),
  }
}

export const mockDataByTimeframe: Record<Timeframe, DashboardData> = {
  Today: {
    metrics: [
      buildMetric('Dials', 45, 'up', '+12%', 30, 5, 'Today'),
      buildMetric('Conversations', 28, 'up', '+8%', 20, 4, 'Today'),
      buildMetric('Credit Pulls', 12, 'down', '-5%', 15, 3, 'Today'),
      buildMetric('Applications', 8, 'up', '+15%', 5, 2, 'Today'),
      buildMetric('Submissions', 5, 'neutral', '0%', 5, 1, 'Today'),
      buildMetric('Locks', 3, 'up', '+50%', 1, 1, 'Today'),
    ],
    funnel: [
      buildFunnelStage('Pull → App', 67),
      buildFunnelStage('App → Sub', 63),
      buildFunnelStage('Sub → Sign', 80),
      buildFunnelStage('Sign → Lock', 60),
    ],
    projections: {
      lockTarget: 12,
      lockActual: 8,
      commissionTarget: 25000,
      commissionActual: 18500,
      pipelineCoverage: 1.8,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 67, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 63, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 80, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Michael Chang',
        dials: 52,
        conversations: 34,
        creditPulls: 18,
        applications: 14,
        locks: 15,
        submissions: 22,
        score: 98,
      },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        dials: 48,
        conversations: 31,
        creditPulls: 16,
        applications: 12,
        locks: 14,
        submissions: 20,
        score: 95,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        dials: 45,
        conversations: 28,
        creditPulls: 14,
        applications: 10,
        locks: 12,
        submissions: 18,
        score: 92,
        isCurrentUser: true,
      },
      {
        rank: 4,
        name: 'Emily Davis',
        dials: 42,
        conversations: 26,
        creditPulls: 12,
        applications: 9,
        locks: 11,
        submissions: 19,
        score: 89,
      },
      {
        rank: 5,
        name: 'David Wilson',
        dials: 38,
        conversations: 22,
        creditPulls: 10,
        applications: 8,
        locks: 10,
        submissions: 15,
        score: 85,
      },
      {
        rank: 6,
        name: 'Jessica Taylor',
        dials: 35,
        conversations: 20,
        creditPulls: 9,
        applications: 7,
        locks: 9,
        submissions: 14,
        score: 82,
      },
      {
        rank: 7,
        name: 'James Brown',
        dials: 32,
        conversations: 18,
        creditPulls: 8,
        applications: 6,
        locks: 8,
        submissions: 12,
        score: 78,
      },
      {
        rank: 8,
        name: 'Linda Martinez',
        dials: 28,
        conversations: 15,
        creditPulls: 7,
        applications: 5,
        locks: 7,
        submissions: 11,
        score: 75,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        dials: 25,
        conversations: 12,
        creditPulls: 6,
        applications: 4,
        locks: 6,
        submissions: 10,
        score: 70,
      },
      {
        rank: 10,
        name: 'William Thomas',
        dials: 20,
        conversations: 10,
        creditPulls: 5,
        applications: 3,
        locks: 5,
        submissions: 8,
        score: 65,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: false, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 7 },
    },
    pipeline: {
      activePipeline: 4250000,
      fundingsMTD: 1850000,
      projectedCurrentMonth: 3200000,
      projectedNextMonth: 3800000,
      details: {
        activeRecords: generatePipelineRecords('active', 4250000),
        fundingRecords: generatePipelineRecords('funded', 1850000),
        projectedCurrentRecords: generatePipelineRecords('projected', 3200000),
        projectedNextRecords: generatePipelineRecords('projected', 3800000),
      },
    },
    urgentActions: {
      finalCDSigningTimes: 4,
      suspendedLoans: 2,
      initialCDsNotSigned: 7,
      initialDisclosuresNotSigned: 5,
    },
    opportunities: {
      retentionRecapture: { count: 18, volume: 5400000 },
      hotLeads: { count: 12, volume: 3600000 },
    },
  },
  'Last 7 Days': {
    metrics: [
      buildMetric('Dials', 315, 'up', '+18%', 250, 20, 'Last 7 Days'),
      buildMetric('Conversations', 196, 'up', '+12%', 150, 15, 'Last 7 Days'),
      buildMetric('Credit Pulls', 84, 'up', '+5%', 70, 8, 'Last 7 Days'),
      buildMetric('Applications', 56, 'up', '+22%', 40, 5, 'Last 7 Days'),
      buildMetric('Submissions', 35, 'up', '+10%', 25, 4, 'Last 7 Days'),
      buildMetric('Locks', 21, 'up', '+40%', 10, 3, 'Last 7 Days'),
    ],
    funnel: [
      buildFunnelStage('Pull → App', 72),
      buildFunnelStage('App → Sub', 68),
      buildFunnelStage('Sub → Sign', 85),
      buildFunnelStage('Sign → Lock', 65),
    ],
    projections: {
      lockTarget: 25,
      lockActual: 21,
      commissionTarget: 50000,
      commissionActual: 42000,
      pipelineCoverage: 2.1,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 72, target: 70, status: 'above' },
      { name: 'App-to-Sub %', actual: 68, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 85, target: 75, status: 'above' },
      { name: 'Overall %', actual: 28, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Sarah Mitchell',
        dials: 350,
        conversations: 230,
        creditPulls: 118,
        applications: 92,
        locks: 32,
        submissions: 45,
        score: 99,
      },
      {
        rank: 2,
        name: 'Michael Chang',
        dials: 330,
        conversations: 215,
        creditPulls: 108,
        applications: 82,
        locks: 28,
        submissions: 40,
        score: 96,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        dials: 310,
        conversations: 195,
        creditPulls: 96,
        applications: 72,
        locks: 25,
        submissions: 38,
        score: 94,
        isCurrentUser: true,
      },
      {
        rank: 4,
        name: 'Emily Davis',
        dials: 290,
        conversations: 180,
        creditPulls: 85,
        applications: 64,
        locks: 22,
        submissions: 35,
        score: 90,
      },
      {
        rank: 5,
        name: 'David Wilson',
        dials: 265,
        conversations: 155,
        creditPulls: 72,
        applications: 56,
        locks: 20,
        submissions: 30,
        score: 86,
      },
      {
        rank: 6,
        name: 'Jessica Taylor',
        dials: 245,
        conversations: 140,
        creditPulls: 64,
        applications: 50,
        locks: 18,
        submissions: 28,
        score: 83,
      },
      {
        rank: 7,
        name: 'James Brown',
        dials: 220,
        conversations: 125,
        creditPulls: 56,
        applications: 42,
        locks: 15,
        submissions: 25,
        score: 79,
      },
      {
        rank: 8,
        name: 'Linda Martinez',
        dials: 195,
        conversations: 105,
        creditPulls: 48,
        applications: 36,
        locks: 14,
        submissions: 22,
        score: 76,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        dials: 175,
        conversations: 85,
        creditPulls: 42,
        applications: 28,
        locks: 12,
        submissions: 20,
        score: 72,
      },
      {
        rank: 10,
        name: 'William Thomas',
        dials: 140,
        conversations: 70,
        creditPulls: 35,
        applications: 22,
        locks: 10,
        submissions: 18,
        score: 68,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 14 },
    },
    pipeline: {
      activePipeline: 4250000,
      fundingsMTD: 1850000,
      projectedCurrentMonth: 3200000,
      projectedNextMonth: 3800000,
      details: {
        activeRecords: generatePipelineRecords('active', 4250000),
        fundingRecords: generatePipelineRecords('funded', 1850000),
        projectedCurrentRecords: generatePipelineRecords('projected', 3200000),
        projectedNextRecords: generatePipelineRecords('projected', 3800000),
      },
    },
    urgentActions: {
      finalCDSigningTimes: 6,
      suspendedLoans: 3,
      initialCDsNotSigned: 9,
      initialDisclosuresNotSigned: 8,
    },
    opportunities: {
      retentionRecapture: { count: 24, volume: 7200000 },
      hotLeads: { count: 16, volume: 4800000 },
    },
  },
  'Last 30 Days': {
    metrics: [
      buildMetric('Dials', 1250, 'down', '-2%', 1300, 50, 'Last 30 Days'),
      buildMetric('Conversations', 780, 'up', '+4%', 700, 40, 'Last 30 Days'),
      buildMetric('Credit Pulls', 340, 'down', '-8%', 380, 20, 'Last 30 Days'),
      buildMetric('Applications', 210, 'up', '+5%', 190, 15, 'Last 30 Days'),
      buildMetric('Submissions', 145, 'up', '+12%', 120, 10, 'Last 30 Days'),
      buildMetric('Locks', 85, 'up', '+18%', 65, 8, 'Last 30 Days'),
    ],
    funnel: [
      buildFunnelStage('Pull → App', 62),
      buildFunnelStage('App → Sub', 69),
      buildFunnelStage('Sub → Sign', 82),
      buildFunnelStage('Sign → Lock', 71),
    ],
    projections: {
      lockTarget: 100,
      lockActual: 85,
      commissionTarget: 200000,
      commissionActual: 175000,
      pipelineCoverage: 2.4,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 62, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 69, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 82, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Michael Chang',
        dials: 1380,
        conversations: 910,
        creditPulls: 480,
        applications: 370,
        locks: 110,
        submissions: 180,
        score: 97,
      },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        dials: 1300,
        conversations: 850,
        creditPulls: 440,
        applications: 340,
        locks: 105,
        submissions: 175,
        score: 96,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        dials: 1200,
        conversations: 770,
        creditPulls: 385,
        applications: 280,
        locks: 95,
        submissions: 160,
        score: 93,
        isCurrentUser: true,
      },
      {
        rank: 4,
        name: 'Emily Davis',
        dials: 1120,
        conversations: 710,
        creditPulls: 330,
        applications: 250,
        locks: 88,
        submissions: 150,
        score: 89,
      },
      {
        rank: 5,
        name: 'David Wilson',
        dials: 1020,
        conversations: 600,
        creditPulls: 275,
        applications: 220,
        locks: 82,
        submissions: 140,
        score: 85,
      },
      {
        rank: 6,
        name: 'Jessica Taylor',
        dials: 940,
        conversations: 545,
        creditPulls: 245,
        applications: 190,
        locks: 75,
        submissions: 130,
        score: 81,
      },
      {
        rank: 7,
        name: 'James Brown',
        dials: 860,
        conversations: 490,
        creditPulls: 215,
        applications: 165,
        locks: 68,
        submissions: 115,
        score: 77,
      },
      {
        rank: 8,
        name: 'Linda Martinez',
        dials: 750,
        conversations: 410,
        creditPulls: 190,
        applications: 140,
        locks: 60,
        submissions: 105,
        score: 74,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        dials: 670,
        conversations: 330,
        creditPulls: 165,
        applications: 110,
        locks: 55,
        submissions: 95,
        score: 70,
      },
      {
        rank: 10,
        name: 'William Thomas',
        dials: 540,
        conversations: 275,
        creditPulls: 135,
        applications: 85,
        locks: 48,
        submissions: 85,
        score: 65,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: true, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Michael Chang', locks: 28 },
    },
    pipeline: {
      activePipeline: 5100000,
      fundingsMTD: 2750000,
      projectedCurrentMonth: 3600000,
      projectedNextMonth: 4200000,
      details: {
        activeRecords: generatePipelineRecords('active', 5100000),
        fundingRecords: generatePipelineRecords('funded', 2750000),
        projectedCurrentRecords: generatePipelineRecords('projected', 3600000),
        projectedNextRecords: generatePipelineRecords('projected', 4200000),
      },
    },
    urgentActions: {
      finalCDSigningTimes: 8,
      suspendedLoans: 5,
      initialCDsNotSigned: 12,
      initialDisclosuresNotSigned: 11,
    },
    opportunities: {
      retentionRecapture: { count: 42, volume: 12600000 },
      hotLeads: { count: 28, volume: 8400000 },
    },
  },
  'Month to Date': {
    metrics: [
      buildMetric('Dials', 650, 'up', '+5%', 600, 30, 'Month to Date'),
      buildMetric('Conversations', 410, 'up', '+8%', 350, 20, 'Month to Date'),
      buildMetric('Credit Pulls', 180, 'up', '+12%', 150, 15, 'Month to Date'),
      buildMetric('Applications', 115, 'up', '+15%', 90, 10, 'Month to Date'),
      buildMetric('Submissions', 75, 'up', '+20%', 55, 8, 'Month to Date'),
      buildMetric('Locks', 45, 'up', '+25%', 30, 5, 'Month to Date'),
    ],
    funnel: [
      buildFunnelStage('Pull → App', 64),
      buildFunnelStage('App → Sub', 65),
      buildFunnelStage('Sub → Sign', 88),
      buildFunnelStage('Sign → Lock', 68),
    ],
    projections: {
      lockTarget: 60,
      lockActual: 45,
      commissionTarget: 120000,
      commissionActual: 95000,
      pipelineCoverage: 2.2,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 64, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 65, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 88, target: 75, status: 'above' },
      { name: 'Overall %', actual: 25, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Sarah Mitchell',
        dials: 780,
        conversations: 510,
        creditPulls: 270,
        applications: 210,
        locks: 55,
        submissions: 90,
        score: 98,
      },
      {
        rank: 2,
        name: 'Michael Chang',
        dials: 720,
        conversations: 465,
        creditPulls: 240,
        applications: 185,
        locks: 52,
        submissions: 85,
        score: 95,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        dials: 675,
        conversations: 420,
        creditPulls: 210,
        applications: 155,
        locks: 48,
        submissions: 80,
        score: 92,
        isCurrentUser: true,
      },
      {
        rank: 4,
        name: 'Emily Davis',
        dials: 630,
        conversations: 390,
        creditPulls: 180,
        applications: 135,
        locks: 42,
        submissions: 75,
        score: 88,
      },
      {
        rank: 5,
        name: 'David Wilson',
        dials: 570,
        conversations: 330,
        creditPulls: 150,
        applications: 120,
        locks: 38,
        submissions: 65,
        score: 84,
      },
      {
        rank: 6,
        name: 'Jessica Taylor',
        dials: 525,
        conversations: 300,
        creditPulls: 135,
        applications: 105,
        locks: 35,
        submissions: 60,
        score: 81,
      },
      {
        rank: 7,
        name: 'James Brown',
        dials: 480,
        conversations: 270,
        creditPulls: 120,
        applications: 90,
        locks: 30,
        submissions: 55,
        score: 76,
      },
      {
        rank: 8,
        name: 'Linda Martinez',
        dials: 420,
        conversations: 225,
        creditPulls: 105,
        applications: 75,
        locks: 28,
        submissions: 50,
        score: 73,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        dials: 375,
        conversations: 180,
        creditPulls: 90,
        applications: 60,
        locks: 25,
        submissions: 45,
        score: 69,
      },
      {
        rank: 10,
        name: 'William Thomas',
        dials: 300,
        conversations: 150,
        creditPulls: 75,
        applications: 45,
        locks: 20,
        submissions: 35,
        score: 64,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Sarah Mitchell', locks: 18 },
    },
    pipeline: {
      activePipeline: 4800000,
      fundingsMTD: 2200000,
      projectedCurrentMonth: 3400000,
      projectedNextMonth: 4000000,
      details: {
        activeRecords: generatePipelineRecords('active', 4800000),
        fundingRecords: generatePipelineRecords('funded', 2200000),
        projectedCurrentRecords: generatePipelineRecords('projected', 3400000),
        projectedNextRecords: generatePipelineRecords('projected', 4000000),
      },
    },
    urgentActions: {
      finalCDSigningTimes: 5,
      suspendedLoans: 3,
      initialCDsNotSigned: 10,
      initialDisclosuresNotSigned: 7,
    },
    opportunities: {
      retentionRecapture: { count: 32, volume: 9600000 },
      hotLeads: { count: 20, volume: 6000000 },
    },
  },
  'Custom Range': {
    metrics: [
      buildMetric('Dials', 850, 'neutral', '0%', 850, 10, 'Custom Range'),
      buildMetric(
        'Conversations',
        520,
        'neutral',
        '0%',
        520,
        10,
        'Custom Range',
      ),
      buildMetric('Credit Pulls', 240, 'neutral', '0%', 240, 5, 'Custom Range'),
      buildMetric('Applications', 160, 'neutral', '0%', 160, 5, 'Custom Range'),
      buildMetric('Submissions', 105, 'neutral', '0%', 105, 3, 'Custom Range'),
      buildMetric('Locks', 65, 'neutral', '0%', 65, 2, 'Custom Range'),
    ],
    funnel: [
      buildFunnelStage('Pull → App', 66),
      buildFunnelStage('App → Sub', 65),
      buildFunnelStage('Sub → Sign', 84),
      buildFunnelStage('Sign → Lock', 73),
    ],
    projections: {
      lockTarget: 80,
      lockActual: 65,
      commissionTarget: 160000,
      commissionActual: 135000,
      pipelineCoverage: 2.0,
    },
    scorecard: [
      { name: 'Pull-to-App %', actual: 66, target: 70, status: 'below' },
      { name: 'App-to-Sub %', actual: 65, target: 60, status: 'above' },
      { name: 'Sub-to-Sign %', actual: 84, target: 75, status: 'above' },
      { name: 'Overall %', actual: 27, target: 20, status: 'above' },
    ],
    leaderboard: [
      {
        rank: 1,
        name: 'Michael Chang',
        dials: 960,
        conversations: 630,
        creditPulls: 335,
        applications: 260,
        locks: 85,
        submissions: 130,
        score: 96,
      },
      {
        rank: 2,
        name: 'Sarah Mitchell',
        dials: 900,
        conversations: 580,
        creditPulls: 300,
        applications: 230,
        locks: 82,
        submissions: 125,
        score: 95,
      },
      {
        rank: 3,
        name: 'Alex Johnson',
        dials: 840,
        conversations: 525,
        creditPulls: 265,
        applications: 195,
        locks: 75,
        submissions: 115,
        score: 92,
        isCurrentUser: true,
      },
      {
        rank: 4,
        name: 'Emily Davis',
        dials: 780,
        conversations: 485,
        creditPulls: 225,
        applications: 170,
        locks: 68,
        submissions: 105,
        score: 88,
      },
      {
        rank: 5,
        name: 'David Wilson',
        dials: 710,
        conversations: 415,
        creditPulls: 190,
        applications: 150,
        locks: 62,
        submissions: 95,
        score: 85,
      },
      {
        rank: 6,
        name: 'Jessica Taylor',
        dials: 650,
        conversations: 375,
        creditPulls: 170,
        applications: 130,
        locks: 58,
        submissions: 90,
        score: 82,
      },
      {
        rank: 7,
        name: 'James Brown',
        dials: 595,
        conversations: 340,
        creditPulls: 150,
        applications: 115,
        locks: 52,
        submissions: 80,
        score: 78,
      },
      {
        rank: 8,
        name: 'Linda Martinez',
        dials: 525,
        conversations: 285,
        creditPulls: 130,
        applications: 95,
        locks: 48,
        submissions: 75,
        score: 75,
      },
      {
        rank: 9,
        name: 'Robert Anderson',
        dials: 465,
        conversations: 225,
        creditPulls: 115,
        applications: 75,
        locks: 42,
        submissions: 65,
        score: 71,
      },
      {
        rank: 10,
        name: 'William Thomas',
        dials: 375,
        conversations: 185,
        creditPulls: 95,
        applications: 58,
        locks: 35,
        submissions: 55,
        score: 66,
      },
    ],
    gamification: {
      streaks: { daily: 12, pull: 5, submission: 3, lock: 2 },
      badges: [
        { name: 'Conversion Master', earned: true, icon: 'Award' },
        { name: 'Pipeline Builder', earned: true, icon: 'Target' },
        { name: 'Lock Machine', earned: true, icon: 'Crown' },
        { name: 'Speed Closer', earned: false, icon: 'Zap' },
      ],
      closerOfWeek: { name: 'Michael Chang', locks: 22 },
    },
    pipeline: {
      activePipeline: 4600000,
      fundingsMTD: 2100000,
      projectedCurrentMonth: 3300000,
      projectedNextMonth: 3900000,
      details: {
        activeRecords: generatePipelineRecords('active', 4600000),
        fundingRecords: generatePipelineRecords('funded', 2100000),
        projectedCurrentRecords: generatePipelineRecords('projected', 3300000),
        projectedNextRecords: generatePipelineRecords('projected', 3900000),
      },
    },
    urgentActions: {
      finalCDSigningTimes: 7,
      suspendedLoans: 4,
      initialCDsNotSigned: 11,
      initialDisclosuresNotSigned: 9,
    },
    opportunities: {
      retentionRecapture: { count: 36, volume: 10800000 },
      hotLeads: { count: 22, volume: 6600000 },
    },
  },
}
